import { SubscriptionType } from '@/@types/enums/SubscriptionType';
import apiService from '@/services/ApiService';
import store from '@/store';
import { commitSetCompanyPaddle } from '@/store/commits/companyCommits';
import { CheckoutEventNames, PaddleEventData } from '@paddle/paddle-js';
import Vue from 'vue';
import { Loading } from 'element-ui';
import VueRouter from 'vue-router';
import { isGuidesInLocation, isSnaploreInLocation } from '@/helpers/appContext';
import { getProductsUrl, getSnaploreAppUrl } from '@/helpers/productsUrl';
import notificationService from '@/services/NotificationService';

const POLL_INTERVAL_MS = 3000;
const SLOW_POLL_INTERVAL_MS = 15000;
// M9: a stalled status webhook must not leave the user under a blocking loader
// indefinitely. Paddle retries a rejected/late webhook minutes later, so after
// the fast phase we release the loader, offer "Continue", and keep polling slowly.
const FAST_POLL_WINDOW_MS = 2 * 60 * 1000;
const POLL_GIVE_UP_MS = 10 * 60 * 1000;

/** Read by CheckoutPage to show a "Continue" button once confirmation is slow. */
export const checkoutStatus = Vue.observable({ slowConfirmation: false });

class PaddleEventHandler {
  public paddleEventHandlers: Partial<Record<string, (data: PaddleEventData, router: VueRouter) => void>>;
  private pollIntervalId: ReturnType<typeof setInterval> | null = null;
  private pollTimeoutId: ReturnType<typeof setTimeout> | null = null;
  private loader: { close: () => void } | null = null;
  // Set once Paddle reported checkout.completed: from then on the customer HAS paid, so a late
  // checkout.error/closed must neither stop the confirmation poll nor claim the checkout failed.
  private paymentCompleted = false;

  constructor() {
    this.paddleEventHandlers = {
      [CheckoutEventNames.CHECKOUT_COMPLETED]: this.handleCheckoutCompleted,
      [CheckoutEventNames.CHECKOUT_CLOSED]: this.handleCheckoutClosed,
      [CheckoutEventNames.CHECKOUT_ERROR]: this.handleCheckoutError,
      [CheckoutEventNames.CHECKOUT_FAILED]: this.handleCheckoutError,
    };
  }

  stop = () => {
    this.paymentCompleted = false;
    this.stopPolling();
  };

  private stopPolling = () => {
    if (this.pollIntervalId) {
      clearInterval(this.pollIntervalId);
      this.pollIntervalId = null;
    }
    if (this.pollTimeoutId) {
      clearTimeout(this.pollTimeoutId);
      this.pollTimeoutId = null;
    }
    this.closeLoader();
    checkoutStatus.slowConfirmation = false;
  };

  private closeLoader = () => {
    if (this.loader) {
      this.loader.close();
      this.loader = null;
    }
  };

  /** M9: the overlay was dismissed before completion — nothing was charged. */
  handleCheckoutClosed = () => {
    // Paddle can emit checkout.closed after checkout.completed; the payment
    // confirmation poll must survive that.
    if (this.paymentCompleted || this.pollIntervalId) return;
    this.stopPolling();
  };

  /** M9: `checkout.error` / `checkout.failed` — stop polling, tell the user. */
  handleCheckoutError = () => {
    // Same guard as handleCheckoutClosed: after checkout.completed the customer has paid, so
    // "Checkout failed" would be wrong and stopping the poll would strand them.
    if (this.paymentCompleted || this.pollIntervalId) return;
    this.stopPolling();
    notificationService.error('Checkout failed. Please try again or contact support.');
  };

  handleCheckoutCompleted = () => {
    // Paddle can emit checkout.completed more than once; a second call would
    // start another interval and orphan the first one past the timeout.
    if (this.pollIntervalId) return;
    this.paymentCompleted = true;

    this.loader = Loading.service({
      lock: true,
      text: 'Updating company',
    });

    const company = store.state.company;

    const checkPaymentStatus = async () => {
      const resPaddleStatus = await apiService.company.getPaidStatus();
      return resPaddleStatus.data ?? null;
    };

    const poll = async () => {
      const paidStatus = await checkPaymentStatus();
      const { hasGuidesAccess, hasSnaploreAccess, ...paddleRes } = paidStatus ?? ({} as NonNullable<typeof paidStatus>);

      // A company paying for the other product already shows `active`, so these two wait for the product bought (SNAP-2241).
      let isConfirmed = paddleRes.status === SubscriptionType.ACTIVE || paddleRes.status === SubscriptionType.TRIALING;
      if (isGuidesInLocation()) isConfirmed = hasGuidesAccess === true;
      if (isSnaploreInLocation()) isConfirmed = hasSnaploreAccess === true;

      if (isConfirmed) {
        this.stopPolling();

        // The billing host is public, so the store has no company if the session could not be loaded. Throwing
        // here, with polling already stopped, would leave someone who has paid on this page.
        if (store.state.company) commitSetCompanyPaddle(paddleRes);

        if (isGuidesInLocation()) {
          // Guides checkout (?app=guides on the checkout URL): land on the
          // Guides app instead of the legacy company home.
          window.location.href = getProductsUrl();
        } else {
          // H7: same origin-aware helper BackSnaploreButton uses — the old
          // `http://localhost:${port}` fallback stranded users on the billing host.
          window.location.href = getSnaploreAppUrl(company?.domain ? company.getDomainAddress() : null);
        }
      }
    };

    this.pollIntervalId = setInterval(poll, POLL_INTERVAL_MS);

    // After the fast window: release the loader, offer "Continue", poll slowly.
    this.pollTimeoutId = setTimeout(() => {
      this.closeLoader();
      checkoutStatus.slowConfirmation = true;
      notificationService.warning(
        "Payment received. Confirmation is taking longer than usual; we'll take you in automatically, or press Continue."
      );
      if (this.pollIntervalId) clearInterval(this.pollIntervalId);
      this.pollIntervalId = setInterval(poll, SLOW_POLL_INTERVAL_MS);

      this.pollTimeoutId = setTimeout(() => {
        if (this.pollIntervalId) clearInterval(this.pollIntervalId);
        this.pollIntervalId = null;
        this.pollTimeoutId = null;
        // Do not give up silently: the customer paid, tell them where things stand.
        notificationService.warning(
          'We could not confirm your payment yet. It was received; refresh this page in a few minutes or contact support if your plan does not appear.'
        );
      }, POLL_GIVE_UP_MS - FAST_POLL_WINDOW_MS);
    }, FAST_POLL_WINDOW_MS);
  };
}

const paddleEventHandler = new PaddleEventHandler();
/** Stops any running confirmation poll (used by tests and page teardown). */
export const stopCheckoutPolling = (): void => paddleEventHandler.stop();

export default paddleEventHandler.paddleEventHandlers;
